import { ref, onMounted, computed, watch } from 'vue'
import axios from 'axios'
//import { getUserInfo } from '@/services/auth'
import { netejaDescripcio, formatNumber, formatDateRange } from '@/utils/text-utils'
import { useRouter, useRoute } from 'vue-router'
import { estats, estatActual, calcularEstatActual } from '@/composables/CampaignList/UseEstatsCamp.js'

const cardsPerPage = 10
const currentPage = ref(1)

const token = localStorage.getItem('token')

if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

const agrupacions = ref([])

export default function useCampanyes() {
  const router = useRouter()
  const route = useRoute()
  const ordres = ref([])
  const search = ref('')
  const loading = ref(true)
  const admin = ref(false)
  const ClientId = ref(0)
  const vista = ref(localStorage.getItem('vistaCampanyes') || 'cards')
  const clientSession = ref([])
  const botigaFiltre = ref(route.params.shop || '')
  const descripcioFiltre = ref(route.params.desc || '')

  const ClientLogo = ref('')
  const ClientImage = ref('')
  const brokenImages = ref({})

  const normalizeClientMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return ''

    const trimmed = url.trim()

    if (!trimmed) return ''
    if (trimmed.startsWith('/storage/') || trimmed.startsWith('/images/')) return trimmed
    if (trimmed.startsWith('storage/') || trimmed.startsWith('images/')) return `/${trimmed}`

    const storageMatch = trimmed.match(/https?:\/\/[^/]+(\/storage\/.+)$/i)
    if (storageMatch) return storageMatch[1]

    const imagesMatch = trimmed.match(/https?:\/\/[^/]+(\/images\/.+)$/i)
    if (imagesMatch) return imagesMatch[1]

    return trimmed
  }

  const getClientFallbackImage = () => {
    return normalizeClientMediaUrl(ClientImage.value)
      || normalizeClientMediaUrl(ClientLogo.value)
      || '/images/logo_negre.png'
  }

  const getCampanyaKey = (campanya) => {
    return String(campanya?.idCamp ?? campanya?.id ?? campanya?.name ?? '')
  }

  const getCardImage = (campanya) => {
    const key = getCampanyaKey(campanya)

    if (key && brokenImages.value[key]) {
      return getClientFallbackImage()
    }

    return normalizeClientMediaUrl(campanya?.img) || getClientFallbackImage()
  }

  const markImageAsBroken = (campanya) => {
    const key = getCampanyaKey(campanya)

    if (!key) return

    brokenImages.value = {
      ...brokenImages.value,
      [key]: true,
    }
  }

  // Modal de Admin
  const dialog = ref(false)
  const selectedCampanya = ref(null)
  const usuario = ref('')
  const contrasena = ref('')
  const imatge = ref(null)
  const campaignExists = ref(false)
  const campaignIdLaravel = ref(null)
  const campaignToken = ref('')

  // Modal credencials
  const credencialsDialog = ref(false)
  const infoCredencials = ref(null)

  const headers = [
    { title: '', key: 'imatge', sortable: false },
    // { title: 'Orden', key: 'name' },
    { title: 'Descripción', key: 'description' },
    { title: 'Fecha inicio', key: 'start_date' },
    { title: 'Fecha fin', key: 'end_date' },
    { title: 'Estado', key: 'state' },
  ]

  /**
   * Carrega les campanyes del client des de backend i aplica filtres (botiga, descripció).
   *
   * Flux:
   *  1) Demana al backend el llistat base de campanyes i agrupacions del client (`by-idClient`).
   *  2) Si hi ha botiga seleccionada, demana les campanyes candidates per aquella botiga (`list-filtrat-botiga`).
   *  3) Si hi ha descripció informada, demana les campanyes candidates per aquella descripció (`list-filtrat-desc`).
   *  4) Passa totes aquestes dades a `getCampanyesNetes`, que s'encarrega de:
   *      - aplicar els filtres de botiga + descripció sobre el llistat base
   *      - netejar/formatar les campanyes (descripcions, quantitats, estat)
   *  5) Desa les campanyes "netes" a `ordres` i les agrupacions filtrades a `agrupacions`.
   *
   * Efectes col·laterals:
   *  - Actualitza `ordres.value` i `agrupacions.value`
   *  - Gestiona `loading.value` (spinner/indicador de càrrega)
   *  - Fa logs d'error si alguna crida a la API falla
   */
  async function getCampanyes() {
    loading.value = true

    try {
      const shopId = Number(botigaFiltre.value) || 0
      const desc = descripcioFiltre.value?.trim() || ''

      // 1) Dades base: campanyes i agrupacions del client (sense filtres específics)
      const baseResp = await axios.post('/api/campaignsWeb/by-idClient', {
        idClient: ClientId.value,
      })

      const baseData = baseResp.data || { campanyes: [], agrupacions: [] }

      // 2) Dades filtrades per botiga
      let dataShop = []
      if (shopId > 0) {
        const respShop = await axios.post('/api/campaignsWeb/list-filtrat-botiga', {
          idClient: ClientId.value,
          shop: shopId,
          filtreEstat: 0, //1 si només volem campanyes en START
        })
        dataShop = respShop.data || []
      }

      // 3) Dades filtrades per descripció
      let dataDesc = []
      if (desc) {
        const respDesc = await axios.post('/api/campaignsWeb/list-filtrat-desc', {
          idClient: ClientId.value,
          description: desc,
          filtreEstat: 0, //1 si només volem campanyes en START
        })
        dataDesc = respDesc.data || []
      }

      // 4) Apliquem filtres i netegem dades
      const { dataFinal, ordresNetes } = getCampanyesNetes(
        baseData,
        dataShop,
        dataDesc,
        shopId,
        desc
      )

      // 5) Actualitzem l'estat reactiu
      ordres.value = ordresNetes
      agrupacions.value = dataFinal.agrupacions
    } catch (error) {
      console.error('Error carregant les ordres o imatges:', error)
    } finally {
      loading.value = false
    }
  }

  function getCampanyesNetes(baseData, dataShop, dataDesc, shopId, desc) {
      const toNum = (v) => Number(v) || 0;
      const onlyIfArray = (v) => Array.isArray(v) ? v : [];

      // --- 0) Ens assegurem de treballar amb arrays
      let campanyes = onlyIfArray(baseData.campanyes);
      let agrupacionsLocals = onlyIfArray(baseData.agrupacions);

      // --- 1) Construcció de conjunts d'IDs segons filtres aplicats
      let idsShop = null;
      if (shopId > 0 && dataShop?.length) {
        idsShop = new Set(
          onlyIfArray(dataShop)
            .map((c) => toNum(c.id))
            .filter(Boolean)
        );
      }

      let idsDesc = null;
      if (desc && dataDesc?.length) {
        idsDesc = new Set(
          onlyIfArray(dataDesc)
            .map((c) => toNum(c.id))
            .filter(Boolean)
        );
      }

      // --- 2) Filtrat de campanyes segons botiga i/o descripció
      campanyes = campanyes.filter((c) => {
        const idCamp = toNum(c.idCamp ?? c.id);

        if (!idsShop && !idsDesc) return true;

        const matchShop = idsShop ? idsShop.has(idCamp) : true;
        const matchDesc = idsDesc ? idsDesc.has(idCamp) : true;

        return matchShop && matchDesc;
      });

      // Conjunt d'IDs finals
      const idsFinals = new Set(
        campanyes.map((c) => toNum(c.idCamp ?? c.id))
      );

      // --- 3) Filtrat d’AGRUPACIONS (versió bona)
      // ❗ NO es filtren serveis. Es mantenen tots.
      agrupacionsLocals = agrupacionsLocals.filter((ag) =>
        onlyIfArray(ag.serveis).some((s) =>
          idsFinals.has(toNum(s.idCamp ?? s.id))
        )
      );

      // --- 4) Preparació de dades finals
      const dataFinal = { ...baseData, campanyes, agrupacions: agrupacionsLocals };
      const ordresNetes = dataFinal.campanyes.map((camp) => ({
        ...camp,
        description: netejaDescripcio(camp.descripcio),
        desc_agrupada: netejaDescripcio(camp.desc_agrupada),
        quantity: formatNumber(camp.quantity),
        state: translateState(camp.state, camp.start_date),
      }));

      return { dataFinal, ordresNetes };
    }




  const paginatedCampanyes = computed(() => {
    const start = (currentPage.value - 1) * cardsPerPage
    return filteredCampanyes.value.slice(start, start + cardsPerPage)
  })

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' }
    return new Date(dateString).toLocaleDateString('es-ES', options)
  }

  const openExternalLink = (campanya) => {
    const url = `https://traycco.odoo.rgbconsulting.com/web#id=${campanya.idCamp}&cids=1&menu_id=335&action=455&model=traycco.campaign&view_type=form`
    window.open(url, '_blank')
  }

  const campaignUrl = computed(() => {
    if (!selectedCampanya.value?.idClient || !campaignToken.value) return ''
    return `https://dashboard.traycco.com/cliente/${selectedCampanya.value.idClient}/${campaignToken.value}`
  })

  const copiarCampaignUrl = async () => {
    if (!campaignUrl.value) return

    try {
      await navigator.clipboard.writeText(campaignUrl.value)
    } catch (error) {
      const input = document.createElement('textarea')
      input.value = campaignUrl.value
      input.setAttribute('readonly', '')
      input.style.position = 'absolute'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    }
  }

  const filteredCampanyes = computed(() => {
    const q = search.value.toLowerCase().trim()
    if (!q) return ordres.value

    return ordres.value.filter((item) => {
      return (
        (item.name || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q) ||
        (item.desc_agrupada || '').toLowerCase().includes(q)
      )
    })
  })

  const toggleVista = () => {
    vista.value = vista.value === 'cards' ? 'table' : 'cards'
  }

  const obrirModal = async (campanya) => {
    selectedCampanya.value = campanya
    usuario.value = ''
    contrasena.value = ''
    campaignExists.value = false
    campaignIdLaravel.value = null
    campaignToken.value = campanya.token || ''

    try {
      const response = await axios.get(`/api/campaignsWeb/by-idCamp/${campanya.idCamp}`)
      if (response.data) {
        campaignExists.value = true
        campaignIdLaravel.value = response.data.id
        campaignToken.value = response.data.token || campaignToken.value
      }
    } catch (error) {
      console.log('No existeix la campanya.')
    }

    dialog.value = true
  }

  const enviarCredenciales = async () => {
    const formData = new FormData()
    formData.append('idClient', selectedCampanya.value.idClient)
    formData.append('descripcio', selectedCampanya.value.description)
    formData.append('start_date', selectedCampanya.value.start_date)
    formData.append('end_date', selectedCampanya.value.end_date)
    formData.append('service_type', selectedCampanya.value.service_type)
    formData.append('service_name', selectedCampanya.value.service_name)
    formData.append('state', selectedCampanya.value.state)
    formData.append('idCamp', selectedCampanya.value.idCamp)

    if (imatge.value) {
      formData.append('img', imatge.value)
    }

    try {
      await axios.post(
        `/api/campaignsWeb/${selectedCampanya.value.idCamp}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      dialog.value = false
    } catch (error) {
      console.error('Error al guardar les credencials:', error)
      console.error('Resposta:', error.response?.data)
    }
  }

  const mostrarCredencials = (campanya) => {
    infoCredencials.value = {
      usr: campanya.user,
      pwd: campanya.pwd,
      url_name: campanya.url_name
    }
    credencialsDialog.value = true
  }

  function translateState(state, startDate) {
    const today = new Date()
    const start = new Date(startDate)

    if (state === 'start' && start > today) {
      return 'Por empezar'
    }

    switch (state) {
      case 'invoiced':
      case 'done':
        return 'Finalizada'
      case 'start':
        return 'En curso'
      default:
        return state
    }
  }

  function getEstat(campanya) {
    //console.log (campanya)
    return calcularEstatActual(campanya, agrupacions.value)
  }

  function getColorEstat(campanya, index) {
    const estat = calcularEstatActual(campanya, agrupacions.value)
    /*console.log ("--------------------------------")
    console.log (campanya)
    console.log (estat)*/
    if (estat.estatActual === 4 && index === 4) {
      return '#4caf50'
    }

    if (estat.saltarPrimers && index < 2) {
      return '#f6f5f5'
    }

    return index <= estat.estatActual ? '#8fbdde' : 'grey'
  }

  const buildCampInfoPath = (idCamp) => {
    return botigaFiltre.value
      ? `/cliente/CampInfo/${idCamp}/${botigaFiltre.value}`
      : `/cliente/CampInfo/${idCamp}`
  }


function getServeis(descripcio, idCamp) {
  // 1) Cerquem l’agrupació que contingui aquest idCamp
  const agrupacio = agrupacions.value.find((a) =>
    Array.isArray(a.serveis) &&
    a.serveis.some((s) => s.idCamp == idCamp)
  );

  // 2) Si NO hi ha agrupació → evitar errors
  if (!agrupacio) {
    console.warn(
      `No s'ha trobat agrupació per descripcio="${descripcio}", idCamp=${idCamp}`
    );

    // Esborrem qualsevol info anterior
    localStorage.removeItem(`campanya_${idCamp}_serveis`);

    // Naveguem igualment
    router.push(buildCampInfoPath(idCamp));
    return;
  }

  // 3) Guardem tots els serveis de l'agrupació
  localStorage.setItem(
    `campanya_${idCamp}_serveis`,
    JSON.stringify(agrupacio.serveis)
  );

  // 4) Navegació
  router.push(buildCampInfoPath(idCamp));
}


  onMounted(async () => {
    if (route.params.idClient && route.params.token) {
      try {
        const { data } = await axios.get(`/api/campaignsWeb/token-access/${route.params.idClient}/${route.params.token}`)

        clientSession.value = data.session || {}
        localStorage.setItem('clientSession', JSON.stringify(clientSession.value))
        localStorage.removeItem('token')
        delete axios.defaults.headers.common.Authorization
        delete axios.defaults.headers.common['Authorization']

        admin.value = false
        ClientId.value = clientSession.value['client_id']
        ClientLogo.value = normalizeClientMediaUrl(clientSession.value['logo'])
        ClientImage.value = normalizeClientMediaUrl(clientSession.value['image'])

        router.replace(`/cliente/CampInfo/${data.campaign.idCamp}`)
        return
      } catch (error) {
        console.error('Token de campanya no vàlid:', error)
        loading.value = false
        router.push('/cliente')
        return
      }
    }

    const data = localStorage.getItem('clientSession')
    if (data) {
      clientSession.value = JSON.parse(data)

      if (clientSession.value['role'] == 'admin') {
        admin.value = true
      } else {
        admin.value = false
      }

      ClientId.value = clientSession.value['client_id']
      ClientLogo.value = normalizeClientMediaUrl(clientSession.value['logo'])
      ClientImage.value = normalizeClientMediaUrl(clientSession.value['image'])

      const sessionShopId = clientSession.value['IdBotiga'] || clientSession.value['iIdBotiga']
      if (!botigaFiltre.value && sessionShopId) {
        botigaFiltre.value = String(sessionShopId)
      }
    }

    getCampanyes()
  })

  watch(vista, (novaVista) => {
    localStorage.setItem('vistaCampanyes', novaVista)
  })

  return {
    ordres,
    search,
    vista,
    loading,
    headers,
    admin,
    filteredCampanyes,
    paginatedCampanyes,
    dialog,
    selectedCampanya,
    usuario,
    contrasena,
    imatge,
    campaignUrl,
    copiarCampaignUrl,
    credencialsDialog,
    infoCredencials,
    botigaFiltre,
    formatDate,
    formatDateRange,
    openExternalLink,
    toggleVista,
    obrirModal,
    enviarCredenciales,
    mostrarCredencials,
    estats,
    estatActual,
    agrupacions,
    calcularEstatActual,
    getEstat,
    getColorEstat,
    getServeis,
    ClientLogo,
    ClientImage,
    cardsPerPage,
    currentPage,
    buildCampInfoPath,
    getCardImage,
    markImageAsBroken
  }
}
